import liff from '@line/liff';
import { LIFF_ID, LINE_CHANNEL_ID, LINE_REDIRECT_URI, YJ_ACCESS_TOKEN } from '../configuration';
import UserService from '../apis/UserService';
import { devLog } from './devLogger';
import { DEBUG_MODE } from '../config/debug';

// ── 除錯模式由 src/config/debug.ts 的 DEBUG_MODE 統一控制 ──
const LINE_AUTH_DEBUG = DEBUG_MODE;

const dbg = (msg: string): void => {
  devLog('[LineAuth]', msg);
  if (LINE_AUTH_DEBUG) alert(`[LineAuth]\n${msg}`);
};

export const isInLineBrowser = (): boolean => /Line\//i.test(navigator.userAgent);

const generateRandomString = (length: number): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

export const redirectToOAuthLogin = (): void => {
  window.location.href = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${LINE_CHANNEL_ID}&redirect_uri=${encodeURIComponent(LINE_REDIRECT_URI)}&state=${generateRandomString(5)}&scope=openid%20profile%20email`;
};

/**
 * 使用 LIFF 取得 id_token，換取 YJ token。
 */
export const loginViaLiff = async (
  onSuccess: (yjToken: string) => void,
  onFail?: () => void,
): Promise<void> => {
  dbg(`步驟1: 進入 loginViaLiff\nLIFF_ID=${LIFF_ID}\nUA=${navigator.userAgent.slice(0, 80)}`);
  try {
    await liff.init({ liffId: LIFF_ID });
    dbg(`步驟2: liff.init 成功\nisLoggedIn=${liff.isLoggedIn()}\nisInClient=${liff.isInClient()}`);

    if (!liff.isLoggedIn()) {
      dbg('步驟3: 未登入，呼叫 liff.login()');
      liff.login();
      return;
    }

    const idToken = liff.getIDToken();
    dbg(`步驟3: 已登入\nidToken=${idToken ? '有值(長度' + idToken.length + ')' : 'null'}`);

    if (!idToken) {
      dbg('步驟4: idToken 為空，走 fallback');
      (onFail ?? redirectToOAuthLogin)();
      return;
    }

    dbg('步驟4: 呼叫後端 /api/login ...');
    const response = await UserService.getYjTokenAndStatus2({ token: `Bearer ${idToken}` });
    const data = response?.data?.message;
    dbg(`步驟5: 後端回應\ndata=${JSON.stringify(data).slice(0, 120)}`);

    if (data?.token) {
      localStorage.setItem(YJ_ACCESS_TOKEN, data.token);
      dbg('步驟6: ✅ 登入成功，YJ token 已儲存');
      onSuccess(data.token);
    } else {
      dbg('步驟6: ❌ 後端未回傳 token，走 fallback');
      (onFail ?? redirectToOAuthLogin)();
    }
  } catch (error: any) {
    dbg(`❌ 例外錯誤\n${error?.message ?? String(error)}`);
    (onFail ?? redirectToOAuthLogin)();
  }
};

/**
 * LINE 登入入口：在 LINE 內建瀏覽器使用 LIFF，其他瀏覽器使用 OAuth redirect。
 */
export const handleLineLogin = async (
  onSuccess: (yjToken: string) => void,
  onFail?: () => void,
): Promise<void> => {
  const inLine = isInLineBrowser();
  dbg(`handleLineLogin\nisInLineBrowser=${inLine}`);
  if (inLine) {
    await loginViaLiff(onSuccess, onFail);
  } else {
    redirectToOAuthLogin();
  }
};
